import { styled } from '@mui/material'
import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import { useNavigate } from 'react-router-dom'
import { useStreamChat } from '@/hooks/useStreamChat'
import { useRoleRestrictions } from '@/hooks/permissions/useRoleRestrictions'
import { useFeatureFlags } from '@/optimizely/useFeatureFlags'
import { FeatureFlag } from '@/optimizely/types'
import { useChatUnreadV2 } from '@/chatV2/hooks/useChatUnreadV2'
import MessageIcon from './Icons/MessageIcon'

const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
  padding: '0.5rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: '50px',
  '&:hover, &:focus, &:active': {
    backgroundColor: 'transparent',
  },
}))

// Phones, beside Ask Attorney Share's circle: no extra width around the icon.
const DENSE_SX = { minWidth: { xs: 40, sm: 50 } }

interface MessagesButtonProps {
  dense?: boolean
}

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    fontFamily: 'inherit',
    fontSize: '.875rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2px 6px',
  },
  '& svg': {
    fontSize: '24px',
  },
}))

const MessagesButtonV1 = ({ dense }: MessagesButtonProps) => {
  const { totalUnreadMessages, setMessageDrawerOpen } = useStreamChat()
  const { checkAndBlock } = useRoleRestrictions()

  const handleMessageClick = () => {
    // Case Manager Restrictions validation as part of https://attorney.atlassian.net/browse/MP-3774
    checkAndBlock(() => setMessageDrawerOpen(true))
  }

  return (
    <StyledButton
      onClick={handleMessageClick}
      sx={dense ? DENSE_SX : undefined}
    >
      <StyledBadge
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        variant="rounded"
        badgeContent={totalUnreadMessages}
        color="secondary"
      >
        <MessageIcon />
      </StyledBadge>
    </StyledButton>
  )
}

const MessagesButtonV2 = ({ dense }: MessagesButtonProps) => {
  const navigate = useNavigate()
  const { unreadCounts } = useChatUnreadV2()
  const { checkAndBlock } = useRoleRestrictions()

  const handleMessageClick = () => {
    checkAndBlock(() => navigate('/messages'))
  }

  return (
    <StyledButton
      onClick={handleMessageClick}
      sx={dense ? DENSE_SX : undefined}
    >
      <StyledBadge
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        variant="rounded"
        badgeContent={unreadCounts.total}
        color="secondary"
      >
        <MessageIcon />
      </StyledBadge>
    </StyledButton>
  )
}

const MessagesButton = ({ dense = false }: MessagesButtonProps) => {
  const featureFlags = useFeatureFlags()
  const isChatV2 = featureFlags[FeatureFlag.MARKETPLACE_CHAT_V2]

  if (isChatV2) return <MessagesButtonV2 dense={dense} />
  return <MessagesButtonV1 dense={dense} />
}

export default MessagesButton
